import { useState } from 'react';
import type { PolicyRule } from '../api/client';
import { DomainListEditor } from './DomainListEditor';

export interface PolicyRuleEditorProps {
  rule: PolicyRule;
  onChange: (rule: PolicyRule) => void;
  onDelete: () => void;
}

const EFFECTS = ['ALLOW', 'DENY', 'REQUIRE_APPROVAL', 'TRANSFORM'] as const;
const CAPABILITIES = ['read', 'write', 'delete', 'admin'] as const;
const METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'] as const;

const effectColors: Record<string, string> = {
  ALLOW: 'bg-[rgba(52,211,153,0.12)] text-[var(--color-success)]',
  DENY: 'bg-[rgba(248,113,113,0.12)] text-[var(--color-error)]',
  TRANSFORM: 'bg-[rgba(251,191,36,0.12)] text-[var(--color-warning)]',
  REQUIRE_APPROVAL: 'bg-[rgba(139,92,246,0.12)] text-accent-violet',
};

function toStringArray(val: unknown): string[] {
  if (Array.isArray(val)) return val as string[];
  return [];
}

function fromCommaSeparated(val: string): string[] {
  return val
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function toCommaSeparated(arr: string[]): string {
  return arr.join(', ');
}

export function PolicyRuleEditor({ rule, onChange, onDelete }: PolicyRuleEditorProps) {
  const [expanded, setExpanded] = useState(true);

  function updateField<K extends keyof PolicyRule>(key: K, value: PolicyRule[K]) {
    onChange({ ...rule, [key]: value });
  }

  function updateCondition(key: string, value: unknown) {
    onChange({
      ...rule,
      conditions: { ...rule.conditions, [key]: value },
    });
  }

  function updateApproval(key: string, value: unknown) {
    onChange({
      ...rule,
      approval: { ...rule.approval, scope: rule.approval?.scope ?? '', ttl_seconds: rule.approval?.ttl_seconds ?? 3600, [key]: value },
    });
  }

  function toggleInArray(arr: string[], item: string): string[] {
    return arr.includes(item) ? arr.filter((v) => v !== item) : [...arr, item];
  }

  const tools = toStringArray(rule.conditions.tools);
  const capabilities = toStringArray(rule.conditions.capabilities);
  const domains = toStringArray(rule.conditions.domains);
  const methods = toStringArray(rule.conditions.methods);
  const actors = toStringArray(rule.conditions.actors);
  const platforms = toStringArray(rule.conditions.platforms);

  return (
    <div className="rounded-lg border border-border-dark bg-surface-1">
      {/* Collapsed header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3">
          <svg
            className={`h-4 w-4 text-text-tertiary transition-transform ${expanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          <span className="font-semibold text-text-primary">{rule.name || 'Untitled rule'}</span>
          {rule.priority !== undefined && (
            <span className="text-xs text-text-tertiary">P{rule.priority}</span>
          )}
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${effectColors[rule.effect] || 'bg-surface-2 text-text-primary'}`}
        >
          {rule.effect}
        </span>
      </button>

      {/* Expanded form */}
      {expanded && (
        <div className="border-t border-border-dark px-5 pb-5 pt-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-text-primary">Name *</label>
            <input
              type="text"
              value={rule.name}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Rule name"
              className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-text-primary">Description</label>
            <input
              type="text"
              value={rule.description || ''}
              onChange={(e) => updateField('description', e.target.value || undefined)}
              placeholder="Optional description"
              className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
            />
          </div>

          {/* Effect + Priority row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary">Effect</label>
              <select
                value={rule.effect}
                onChange={(e) => updateField('effect', e.target.value)}
                className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
              >
                {EFFECTS.map((eff) => (
                  <option key={eff} value={eff}>
                    {eff}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary">Priority</label>
              <input
                type="number"
                value={rule.priority ?? ''}
                onChange={(e) =>
                  updateField('priority', e.target.value === '' ? undefined : Number(e.target.value))
                }
                placeholder="e.g., 10"
                className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
              />
            </div>
          </div>

          {/* Conditions section */}
          <div>
            <h4 className="text-sm font-semibold text-text-primary">Conditions</h4>
            <div className="mt-3 space-y-4">
              {/* Tools */}
              <div>
                <label className="block text-sm font-medium text-text-primary">Tools</label>
                <input
                  type="text"
                  value={toCommaSeparated(tools)}
                  onChange={(e) => updateCondition('tools', fromCommaSeparated(e.target.value))}
                  placeholder="http.*, slack.post_message"
                  className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                />
                <p className="mt-0.5 text-xs text-text-tertiary">Comma-separated tool patterns</p>
              </div>

              {/* Capabilities */}
              <div>
                <label className="block text-sm font-medium text-text-primary">Capabilities</label>
                <div className="mt-1.5 flex flex-wrap gap-3">
                  {CAPABILITIES.map((cap) => (
                    <label key={cap} className="flex items-center gap-1.5 text-sm text-text-primary">
                      <input
                        type="checkbox"
                        checked={capabilities.includes(cap)}
                        onChange={() => updateCondition('capabilities', toggleInArray(capabilities, cap))}
                        className="h-4 w-4 rounded border-border-dark bg-surface-2 text-accent-teal focus:ring-accent-teal"
                      />
                      {cap}
                    </label>
                  ))}
                </div>
              </div>

              {/* Domains */}
              <DomainListEditor
                domains={domains}
                onChange={(d) => updateCondition('domains', d)}
                label="Domains"
                placeholder="*.example.com"
              />

              {/* Methods */}
              <div>
                <label className="block text-sm font-medium text-text-primary">Methods</label>
                <div className="mt-1.5 flex flex-wrap gap-3">
                  {METHODS.map((method) => (
                    <label key={method} className="flex items-center gap-1.5 text-sm text-text-primary">
                      <input
                        type="checkbox"
                        checked={methods.includes(method)}
                        onChange={() => updateCondition('methods', toggleInArray(methods, method))}
                        className="h-4 w-4 rounded border-border-dark bg-surface-2 text-accent-teal focus:ring-accent-teal"
                      />
                      {method}
                    </label>
                  ))}
                </div>
              </div>

              {/* Actors */}
              <div>
                <label className="block text-sm font-medium text-text-primary">Actors</label>
                <input
                  type="text"
                  value={toCommaSeparated(actors)}
                  onChange={(e) => updateCondition('actors', fromCommaSeparated(e.target.value))}
                  placeholder="agent-1, user-*"
                  className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                />
                <p className="mt-0.5 text-xs text-text-tertiary">Comma-separated actor patterns</p>
              </div>

              {/* Platforms */}
              <div>
                <label className="block text-sm font-medium text-text-primary">Platforms</label>
                <input
                  type="text"
                  value={toCommaSeparated(platforms)}
                  onChange={(e) => updateCondition('platforms', fromCommaSeparated(e.target.value))}
                  placeholder="langgraph, claude_code"
                  className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                />
                <p className="mt-0.5 text-xs text-text-tertiary">Comma-separated platform identifiers</p>
              </div>
            </div>
          </div>

          {/* Approval config (only for REQUIRE_APPROVAL) */}
          {rule.effect === 'REQUIRE_APPROVAL' && (
            <div>
              <h4 className="text-sm font-semibold text-text-primary">Approval Configuration</h4>
              <div className="mt-3 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-text-primary">Scope</label>
                  <input
                    type="text"
                    value={rule.approval?.scope || ''}
                    onChange={(e) => updateApproval('scope', e.target.value)}
                    placeholder="e.g., production-deploy"
                    className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary">TTL (seconds)</label>
                  <input
                    type="number"
                    value={rule.approval?.ttl_seconds ?? 3600}
                    onChange={(e) => updateApproval('ttl_seconds', Number(e.target.value))}
                    className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text-primary">Reason</label>
                  <input
                    type="text"
                    value={rule.approval?.reason || ''}
                    onChange={(e) => updateApproval('reason', e.target.value || undefined)}
                    placeholder="Why approval is required"
                    className="mt-1 w-full rounded-lg border border-border-dark bg-surface-2 px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-teal focus:outline-none focus:ring-1 focus:ring-accent-teal"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Delete button */}
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={onDelete}
              className="rounded-lg bg-[rgba(248,113,113,0.12)] px-4 py-2 text-sm font-medium text-[var(--color-error)] transition-colors hover:bg-[rgba(248,113,113,0.2)]"
            >
              Delete Rule
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
